/**
 * pathPrefix에서 과목(subject) 추출
 * 예: "theory/db" → "db"
 * 예: "theory/network-os" → "network-os"
 * 예: "theory/security-newtech" → "security-newtech"
 * 예: "past-exam/2025" → "past-exam"
 */
export function extractSubject(pathPrefix: string): string {
  // "theory/db" 형태에서 "db" 추출
  const parts = pathPrefix.split('/');

  // past-exam 하위는 모두 "past-exam"으로 통일
  if (parts[0] === 'past-exam') {
    return 'past-exam';
  }

  if (parts.length >= 2 && parts[0] === 'theory') {
    return parts[1];
  }
  // theory가 아닌 경우 전체 prefix 반환
  return pathPrefix;
}

/**
 * pagePath에서 과목(subject) 추출
 * 예: "/theory/db/normalization" → "db"
 * 예: "/theory/network-os/osi-model" → "network-os"
 * 예: "/past-exam/2025/1" → "past-exam"
 */
export function extractSubjectFromPath(pagePath: string): string {
  // "/theory/db/normalization" 형태에서 "db" 추출
  const parts = pagePath.split('/').filter(Boolean);

  // past-exam 하위는 모두 "past-exam"으로 통일
  if (parts[0] === 'past-exam') {
    return 'past-exam';
  }

  if (parts.length >= 2 && parts[0] === 'theory') {
    return parts[1];
  }
  return parts[0] || '';
}

/**
 * pagePath를 URL safe한 document ID로 인코딩
 * "/" → "_" 로 변환
 */
export function encodePagePath(pagePath: string): string {
  return pagePath.replace(/\//g, '_').replace(/^_/, '');
}

/**
 * 인코딩된 pagePath를 원래 형태로 디코딩
 * "_" → "/" 로 변환
 */
export function decodePagePath(encodedPath: string): string {
  return '/' + encodedPath.replace(/_/g, '/');
}

const SCOPE_TO_SUBJECT: Record<string, string> = {
  sql: "programming", logicDBdesign: "db", physicalDBdesign: "db", transaction: "db",
  test: "sw-dev", interface: "sw-dev", integrationImpl: "sw-dev",
  appDesign: "sw-design", uml: "sw-design", ui: "sw-design", methodology: "sw-design",
  memoryProcess: "network-os", ip: "network-os", network7Layer: "network-os", shellScript: "network-os",
  attackTypes: "security-newtech", cryptographyAlgorithm: "security-newtech", securityFeature: "security-newtech", newTech: "security-newtech",
  java: "programming", python: "programming", c: "programming",
};

export function getForumUrl(problemId: string): string | null {
  const scope = problemId.split("_")[0];
  const subject = SCOPE_TO_SUBJECT[scope];
  if (!subject) return null;
  return `/forum/jcg/${subject}/${scope}/${problemId}`;
}

/**
 * 댓글 slug → 실제 사이트 경로(leading slash 포함).
 *
 * forum 댓글의 slug(`forum/<problemId>`)는 실제 라우트
 * (`/forum/jcg/<subject>/<scope>/<problemId>`)와 다르므로 getForumUrl로 변환한다.
 * scope 매핑이 없으면 slug 기반 경로로 폴백한다.
 * theory/coding 댓글은 slug가 곧 경로라 앞에 "/"만 붙인다.
 */
export function getCommentPagePath(slug: string): string {
  const FORUM_PREFIX = "forum/";
  if (slug.startsWith(FORUM_PREFIX)) {
    const problemId = slug.slice(FORUM_PREFIX.length);
    return getForumUrl(problemId) ?? `/${slug}`;
  }
  return `/${slug}`;
}
